import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { InputSwitchModule } from 'primeng/inputswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { ClinicService } from '../../../service/admin/clinic.service';
import { ClinicScheduleService } from '../../../service/admin/clinic-schedule.service';
import { ToasterService } from '../../../service/common/toaster.service';
import {
  Clinic,
  ClinicWorkingHour,
  SLOT_DURATION_OPTIONS,
  WEEKDAYS,
  createDefaultWorkingHours,
} from '../../../models/clinic.model';

interface DayScheduleRow {
  day: string;
  enabled: boolean;
  open: string;
  close: string;
}

@Component({
  selector: 'app-clinic-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    InputTextareaModule,
    InputSwitchModule,
    ConfirmDialogModule,
  ],
  providers: [ConfirmationService],
  templateUrl: './clinic-management.component.html',
  styleUrls: ['./clinic-management.component.scss'],
})
export class ClinicManagementComponent implements OnInit {
  clinics: Clinic[] = [];
  loading = false;
  saving = false;
  searchTerm = '';

  dialogVisible = false;
  isEditing = false;
  editingClinicId: number | null = null;

  readonly slotDurationOptions = SLOT_DURATION_OPTIONS;
  readonly statusOptions = [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ];

  form = this.createEmptyForm();
  scheduleRows: DayScheduleRow[] = this.buildScheduleRows();
  dialogStep: 'details' | 'schedule' = 'details';

  constructor(
    private clinicService: ClinicService,
    private clinicScheduleService: ClinicScheduleService,
    private toaster: ToasterService,
    private confirmationService: ConfirmationService,
  ) {}

  ngOnInit(): void {
    this.loadClinics();
  }

  get filteredClinics(): Clinic[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.clinics;
    return this.clinics.filter((clinic) => {
      const haystack = [
        clinic.name,
        clinic.city,
        clinic.address,
        clinic.contact_number,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(term);
    });
  }

  loadClinics(): void {
    this.loading = true;
    this.clinicService.getClinic().subscribe({
      next: (res: any) => {
        this.clinics = Array.isArray(res?.data) ? res.data : [];
        this.loading = false;
      },
      error: () => {
        this.clinics = [];
        this.loading = false;
        this.toaster.error('Failed to load clinics.');
      },
    });
  }

  openCreateDialog(): void {
    this.isEditing = false;
    this.editingClinicId = null;
    this.dialogStep = 'details';
    this.form = this.createEmptyForm();
    this.scheduleRows = this.buildScheduleRows(createDefaultWorkingHours());
    this.dialogVisible = true;
  }

  openEditDialog(clinic: Clinic): void {
    this.isEditing = true;
    this.editingClinicId = clinic.id;
    this.dialogStep = 'details';
    this.form = {
      name: clinic.name || '',
      address: clinic.address || '',
      city: clinic.city || '',
      state: clinic.state || '',
      pincode: clinic.pincode || '',
      country: clinic.country || 'India',
      contact_number: clinic.contact_number || '',
      slot_duration_minutes: clinic.slot_duration_minutes || 60,
      status: clinic.status || 'active',
    };
    this.scheduleRows = this.buildScheduleRows(clinic.working_hours || []);
    this.dialogVisible = true;
  }

  saveClinic(): void {
    if (!this.form.name.trim()) {
      this.toaster.warn('Clinic name is required.');
      return;
    }

    const workingHours = this.scheduleRows
      .filter((row) => row.enabled && row.open && row.close)
      .map((row) => ({
        day: row.day,
        open: row.open,
        close: row.close,
      }));

    const invalidDay = this.scheduleRows.find(
      (row) => row.enabled && (!row.open || !row.close || row.open >= row.close),
    );
    if (invalidDay) {
      this.toaster.warn(`Please fix working hours for ${invalidDay.day}.`);
      return;
    }

    const payload: Partial<Clinic> = {
      ...this.form,
      name: this.form.name.trim(),
      working_hours: workingHours,
    };

    this.saving = true;
    const request$ =
      this.isEditing && this.editingClinicId != null
        ? this.clinicService.updateClinic(this.editingClinicId, payload)
        : this.clinicService.createClinic(payload);

    request$.subscribe({
      next: () => {
        this.saving = false;
        this.dialogVisible = false;
        this.toaster.success(this.isEditing ? 'Clinic updated successfully.' : 'Clinic created successfully.');
        this.loadClinics();
      },
      error: (err) => {
        this.saving = false;
        const detail =
          err?.error?.errors?.name?.[0] ||
          err?.error?.message ||
          'Failed to save clinic.';
        this.toaster.error(detail);
      },
    });
  }

  confirmDelete(clinic: Clinic): void {
    this.confirmationService.confirm({
      message: `Delete "${clinic.name}"? This cannot be undone.`,
      header: 'Delete Clinic',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.clinicService.deleteClinic(clinic.id).subscribe({
          next: () => {
            this.toaster.success('Clinic deleted successfully.');
            this.loadClinics();
          },
          error: (err) => {
            this.toaster.error(err?.error?.message || 'Failed to delete clinic.');
          },
        });
      },
    });
  }

  getScheduleSummary(clinic: Clinic): string {
    return this.clinicScheduleService.getScheduleSummary(clinic);
  }

  getSlotLabel(clinic: Clinic): string {
    const minutes = clinic.slot_duration_minutes || 60;
    const option = SLOT_DURATION_OPTIONS.find((item) => item.value === minutes);
    return option?.label || `${minutes} minutes`;
  }

  toggleDay(row: DayScheduleRow, enabled: boolean): void {
    row.enabled = enabled;
    if (enabled && !row.open) {
      row.open = '09:00';
      row.close = '21:00';
    }
  }

  setDialogStep(step: 'details' | 'schedule'): void {
    if (step === 'schedule' && !this.form.name.trim()) {
      this.toaster.warn('Enter a clinic name before configuring the schedule.');
      return;
    }
    this.dialogStep = step;
  }

  getShortDay(day: string): string {
    return day.slice(0, 3);
  }

  getOpenDaysCount(): number {
    return this.scheduleRows.filter((row) => row.enabled).length;
  }

  getSlotDurationLabel(): string {
    const option = SLOT_DURATION_OPTIONS.find((item) => item.value === this.form.slot_duration_minutes);
    return option?.label || `${this.form.slot_duration_minutes} minutes`;
  }

  getDialogPreviewSummary(): string {
    const openRows = this.scheduleRows.filter((row) => row.enabled && row.open && row.close);
    if (!openRows.length) {
      return 'No working days configured';
    }

    const first = openRows[0];
    const sameHours = openRows.every((row) => row.open === first.open && row.close === first.close);
    if (sameHours) {
      return `${openRows.length} day${openRows.length === 1 ? '' : 's'} · ${this.formatDisplayTime(first.open)} – ${this.formatDisplayTime(first.close)}`;
    }

    return `${openRows.length} open days with varied hours`;
  }

  getEstimatedSlotsForRow(row: DayScheduleRow): number {
    if (!row.enabled || !row.open || !row.close || row.open >= row.close) {
      return 0;
    }

    const openMinutes = this.parseTimeToMinutes(row.open);
    const closeMinutes = this.parseTimeToMinutes(row.close);
    const duration = this.form.slot_duration_minutes || 60;
    return Math.max(0, Math.floor((closeMinutes - openMinutes) / duration));
  }

  applyWeekdayHours(): void {
    this.scheduleRows.forEach((row) => {
      const isWeekend = row.day === 'Saturday' || row.day === 'Sunday';
      row.enabled = !isWeekend;
      row.open = '09:00';
      row.close = '21:00';
    });
  }

  applyHoursToAllOpenDays(): void {
    const source = this.scheduleRows.find((row) => row.enabled);
    if (!source) {
      this.toaster.warn('Enable at least one day first.');
      return;
    }

    this.scheduleRows.forEach((row) => {
      if (row.enabled) {
        row.open = source.open;
        row.close = source.close;
      }
    });
  }

  closeAllDays(): void {
    this.scheduleRows.forEach((row) => {
      row.enabled = false;
    });
  }

  setSlotDuration(minutes: number): void {
    this.form.slot_duration_minutes = minutes;
  }

  formatRowHours(row: DayScheduleRow): string {
    if (!row.enabled || !row.open || !row.close) {
      return 'Closed';
    }
    return `${this.formatDisplayTime(row.open)} – ${this.formatDisplayTime(row.close)}`;
  }

  isWeekend(day: string): boolean {
    return day === 'Saturday' || day === 'Sunday';
  }

  private formatDisplayTime(time: string): string {
    const [hours, minutes] = time.split(':').map(Number);
    const suffix = hours >= 12 ? 'PM' : 'AM';
    const displayHour = ((hours + 11) % 12) + 1;
    return `${displayHour}:${String(minutes || 0).padStart(2, '0')} ${suffix}`;
  }

  private parseTimeToMinutes(time: string): number {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + (minutes || 0);
  }

  private createEmptyForm() {
    return {
      name: '',
      address: '',
      city: '',
      state: '',
      pincode: '',
      country: 'India',
      contact_number: '',
      slot_duration_minutes: 60,
      status: 'active' as 'active' | 'inactive',
    };
  }

  private buildScheduleRows(workingHours: ClinicWorkingHour[] = []): DayScheduleRow[] {
    return WEEKDAYS.map((day) => {
      const existing = workingHours.find((entry) => entry.day === day);
      return {
        day,
        enabled: !!(existing?.open && existing?.close),
        open: existing?.open || '09:00',
        close: existing?.close || '21:00',
      };
    });
  }
}
